import { CircularProgress, Grid } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shadowBleed } from '../components/common/cardShadow';
import { HeaderSpacer } from '../components/Header';
import ListInfoCardGrid from '../components/ListInfoCardGrid';
import FilterBar from '../components/FilterBar';
import {
  exitSelectionMode, selectContext, selectContextError, selectContextStatus,
} from '../redux/features/context/contextSlice';
import {
  deleteList, fetchLists, shareList, updateList,
} from '../redux/features/lists/listsSlice';
import { approveList, rejectList, batchModerateLists } from '../redux/features/pending/pendingSlice';
import SelectionBar, { SelectionBarVerdictButton } from '../components/SelectionBar';
import { RejectReasonMenu } from '../components/common/VerdictBar';
import useBatchSelection from '../hooks/useBatchSelection';
import StyledBackdrop from '../components/StyledBackdrop';
import ViewError from '../components/common/ViewError';
import useLoadErrorHandler from '../hooks/useLoadErrorHandler';
import Login from '../components/Login';
import { DELETE_LABEL, UNSHARE_LABEL } from '../common/ActionLabels';
import { mergePublicAndPrivateItems, mustSignIn, shareResultMessage } from '../common/ItemTools';
import { setSnackbar } from '../redux/features/snackbarSlice';
import { batchVerdictMessage, verdictPastTense, verdictResultMessage } from '../common/VerdictMessages';
import useConfirm from '../hooks/useConfirm';
import useGridLayout from '../hooks/useGridLayout';
import usePaging from '../hooks/usePaging';

function ListsView(props) {
  const { namespace, type: propType, view: viewProp } = props;
  const type = propType || 'lists';

  const { pageSize } = useGridLayout();

  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const { userInfo } = useSelector((state) => state.users);
  const status = useSelector(selectContextStatus);
  const loadError = useSelector(selectContextError);
  const onLoadError = useLoadErrorHandler('lists');
  const currContext = useSelector(selectContext);

  const page = +searchParams.get('page');
  const filter = searchParams.get('filter');
  const sortby = searchParams.get('sortby');
  const source = searchParams.get('source');
  const view = viewProp || searchParams.get('view');

  const forceSignIn = mustSignIn(namespace, page, userInfo);

  let context = null;
  if (!forceSignIn && currContext && currContext.type === type
    && currContext.namespace === namespace) {
    context = currContext;
  }

  // Extract all URL parameters - single source of truth
  const urlParams = useMemo(() => ({
    page: page >= 1 ? page : 1,
    filter,
    sortby,
    source,
    view,
  }), [page, filter, sortby, source, view]);

  // Fetch lists based on current URL params
  const fetchCurrentLists = useCallback(() => {
    if (forceSignIn) return;

    const pageNumber = urlParams.page;
    const offset = (pageNumber - 1) * pageSize;

    const params = {
      pageNumber,
      offset,
      limit: pageSize,
      ...(urlParams.filter && { filter: urlParams.filter }),
      ...(urlParams.sortby && { sortby: urlParams.sortby }),
      ...(urlParams.source && { source: urlParams.source }),
      ...(urlParams.view && { view: urlParams.view }),
    };

    dispatch(fetchLists({ namespace, params }))
      .unwrap()
      .catch(onLoadError);
  }, [dispatch, namespace, urlParams, forceSignIn, pageSize, onLoadError]);

  // Single effect: URL changes → fetch data
  useEffect(() => {
    if (!forceSignIn) {
      fetchCurrentLists();
    }
  }, [
    urlParams.page,
    urlParams.filter,
    urlParams.sortby,
    urlParams.source,
    urlParams.view,
    namespace,
    userInfo,
    forceSignIn,
    fetchCurrentLists,
  ]);

  const { handleNextPage, handlePreviousPage } = usePaging(urlParams.page);

  const isModerating = namespace === 'pending';
  const showDelete = Boolean(userInfo) && !isModerating;
  const showUnshare = Boolean(userInfo?.isModerator) && !isModerating;
  const [bulkRejectAnchor, setBulkRejectAnchor] = useState(null);

  const pageLists = useMemo(() => context?.lists || [], [context]);
  const {
    selectionMode, selectedIds, selectedCount,
    pageIds, selectAllLabel, selectPage, selectAll, clear: clearSelection,
  } = useBatchSelection(pageLists);
  const onListOpen = (list) => navigate(
    isModerating ? `/moderate/lists/${list.id}` : `/lists/${list.id}`,
  );
  const onListEdit = (list) => navigate(
    isModerating ? `/moderate/lists/${list.id}?edit` : `/edit/list/${list.id}`,
  );
  const onListShare = (list) => {
    dispatch(shareList({ listId: list.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        fetchCurrentLists();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to share the list.',
        }));
      });
  };
  const onListFavorite = (list) => {
    const { isFavorite } = mergePublicAndPrivateItems(list) || list;
    dispatch(updateList({
      namespace: 'private', listId: list.id, patch: { isFavorite: !isFavorite },
    }))
      .unwrap()
      .then(fetchCurrentLists)
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to favorite the list.',
        }));
      });
  };
  const onListVerdict = (list, kind, reason) => {
    const thunk = kind === 'approve'
      ? approveList(list)
      : rejectList({ id: list.id, reason });
    dispatch(thunk)
      .unwrap()
      .then(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: verdictResultMessage(kind, 'list', mergePublicAndPrivateItems(list)?.title, reason),
        }));
        fetchCurrentLists();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to ${kind} list.`,
        }));
        fetchCurrentLists();
      });
  };

  const onBatchVerdict = async (kind, reason = null) => {
    if (selectedCount === 0) return;
    try {
      const { failed } = await dispatch(
        batchModerateLists({ ids: selectedIds, verdict: kind, reason }),
      ).unwrap();
      if (failed.length > 0) {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'warning',
          snackbarMessage: `${failed.length} of ${selectedIds.length} lists could not be ${verdictPastTense(kind)}.`,
        }));
      } else {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: batchVerdictMessage(kind, 'list', selectedIds.length, reason),
        }));
      }
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: `Failed to ${kind} lists.`,
      }));
    } finally {
      dispatch(exitSelectionMode());
      fetchCurrentLists();
    }
  };
  const onListUnshare = async (list) => {
    const { title } = mergePublicAndPrivateItems(list) || list;
    const ok = await confirm({
      title: 'Unshare list?',
      message: `"${title || 'This list'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteList({ namespace: 'public', listId: list.id }))
      .unwrap()
      .then(() => {
        fetchCurrentLists();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to unshare list.',
        }));
      });
  };
  const onListDelete = async (list) => {
    // The card hands back the raw list, so the title comes from the merge.
    const { title } = mergePublicAndPrivateItems(list) || list;
    const ok = await confirm({
      title: 'Delete list?',
      message: `"${title || 'This list'}" will be permanently removed. The items in it stay in your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteList({ namespace: 'private', listId: list.id }))
      .unwrap()
      .then(() => {
        // Refresh current view after deleting
        fetchCurrentLists();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to delete list.',
        }));
      });
  };

  return (
    <>
      <HeaderSpacer />
      { !forceSignIn && <FilterBar entity="lists" /> }
      { forceSignIn && <Login />}
      { !forceSignIn && (
        <StyledBackdrop open={status === 'loading' || (!context && status !== 'failed')}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      { !forceSignIn && !context && status === 'failed' && (
        <ViewError message={loadError} entity="lists" />
      )}
      { !forceSignIn && context && status !== 'loading' && (
        <Grid container sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...shadowBleed,
        }}>
          <Grid container item sx={{ flex: 1, ...shadowBleed }}>
            <Grid xs={12} item sx={{ height: '100%' }}>
              <ListInfoCardGrid
                currentPage={context}
                listActions={{
                  handlePreviousPage,
                  handleNextPage,
                }}
                listInfoActions={{
                  onListOpen,
                  onListEdit,
                  ...(!isModerating && { onListShare }),
                  ...(!isModerating && Boolean(userInfo) && { onListFavorite }),
                  ...(showDelete && { onListDelete }),
                  ...(showUnshare && { onListUnshare }),
                  ...(isModerating && { onListVerdict }),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      { isModerating && selectionMode && (
        <SelectionBar
          total={pageIds.length}
          selectAllLabel={selectAllLabel}
          onSelectPage={selectPage}
          onSelectAll={selectAll}
          onClear={clearSelection}
          actions={(
            <>
              <SelectionBarVerdictButton kind="approve" onClick={() => onBatchVerdict('approve')}>
                <CheckIcon sx={{ fontSize: 17 }} />
                Approve
              </SelectionBarVerdictButton>
              <SelectionBarVerdictButton
                kind="reject"
                onClick={(event) => setBulkRejectAnchor(event.currentTarget)}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
                Reject
              </SelectionBarVerdictButton>
              <RejectReasonMenu
                anchorEl={bulkRejectAnchor}
                title={`Reject ${selectedCount} as`}
                onClose={() => setBulkRejectAnchor(null)}
                onPick={(reason) => onBatchVerdict('reject', reason)}
              />
            </>
          )}
        />
      )}
      {confirmDialog}
    </>
  );
}

export default ListsView;
